import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  IndianRupee, ShoppingBag, Truck, PackageCheck, X, Loader2,
  Bell, ImagePlus, Trash2, Star, Tag, Plus, Instagram, TrendingUp,
  MessageSquare, Mail, Eye, Ticket, Building2, Newspaper,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api, { inr, resolveImg, formatApiErrorDetail } from "../lib/api";
import AdminEmailTab from "../components/AdminEmailTab";
import AdminTicketsTab from "../components/AdminTicketsTab";
import AdminVendorsTab from "../components/AdminVendorsTab";
import AdminProductsTab from "../components/AdminProductsTab";

const STATUSES = ["Placed", "Confirmed", "Shipped", "Out for Delivery", "Delivered", "Cancelled"];
const FILTERS = ["all", ...STATUSES];

const STATUS_COLORS = {
  Placed: "bg-secondary text-secondary-foreground",
  Confirmed: "bg-accent/20 text-accent-foreground",
  Shipped: "bg-primary/15 text-primary",
  "Out for Delivery": "bg-primary/15 text-primary",
  Delivered: "bg-primary text-primary-foreground",
  Cancelled: "bg-destructive/15 text-destructive",
};

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border p-5 bg-card">
      <Icon size={20} className="text-primary" strokeWidth={1.5} />
      <p className="mt-3 text-2xl font-display text-primary">{value}</p>
      <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

/* ---------------- Insights ---------------- */
function InsightsSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/insights").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="admin-insights">
      <div className="lg:col-span-2 border border-border bg-card p-5">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-muted-foreground mb-4">
          <TrendingUp size={14} className="text-primary" /> Revenue & orders — last 8 weeks
        </p>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={data.weekly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e2d9" vertical={false} />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis
              yAxisId="rev"
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => (v >= 1000 ? `₹${v / 1000}k` : `₹${v}`)}
            />
            <YAxis yAxisId="ord" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v, name) => (name === "Revenue" ? inr(v) : v)} />
            <Bar yAxisId="rev" dataKey="revenue" name="Revenue" fill="#1A3626" radius={[3, 3, 0, 0]} maxBarSize={36} />
            <Line yAxisId="ord" dataKey="orders" name="Orders" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="border border-border bg-card p-5" data-testid="admin-top-sellers">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-4">Top sellers</p>
        {data.top_products.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sales yet.</p>
        ) : (
          <div className="space-y-3">
            {data.top_products.map((p) => (
              <div key={p.product_id} className="flex items-center gap-3" data-testid={`top-product-${p.product_id}`}>
                <div className="w-10 aspect-square bg-muted overflow-hidden shrink-0">
                  {p.image ? <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-tight line-clamp-1">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.qty} sold</p>
                </div>
                <span className="text-sm font-semibold text-primary">{inr(p.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Orders tab ---------------- */
function OrdersTab() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [newCount, setNewCount] = useState(0);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders", { params: { status: filter } }).then((r) => setOrders(r.data));
    api.get("/admin/new-count").then((r) => setNewCount(r.data.count));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markSeen = async () => {
    await api.post("/admin/orders/mark-seen");
    toast.success("Marked all as seen");
    load();
  };

  const updateStatus = async (order, status) => {
    setUpdating(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status });
      toast.success(`Order marked ${status}`);
      setSelected({ ...order, status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Stat icon={ShoppingBag} label="Total Orders" value={stats.total_orders} />
          <Stat icon={IndianRupee} label="Revenue" value={inr(stats.revenue)} />
          <Stat icon={ShoppingBag} label="Pending" value={stats.pending} />
          <Stat icon={Truck} label="In Transit" value={stats.shipped} />
          <Stat icon={PackageCheck} label="Delivered" value={stats.delivered} />
        </div>
      )}

      <InsightsSection />

      {newCount > 0 && (
        <div className="flex items-center justify-between gap-4 bg-accent/15 border border-accent/40 px-5 py-3 mb-6" data-testid="admin-new-banner">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <Bell size={16} className="text-accent" />
            You have <strong>{newCount}</strong> new order{newCount > 1 ? "s" : ""} to review.
          </span>
          <button onClick={markSeen} className="text-sm font-semibold text-primary link-underline" data-testid="admin-mark-seen">
            Mark all as seen
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm border transition-colors capitalize ${
              filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"
            }`}
            data-testid={`admin-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left">
            <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 hidden md:table-cell">Date</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3 hidden sm:table-cell">Payment</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No orders in this view.</td></tr>
            ) : (
              orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => setSelected(o)}
                  className="border-t border-border hover:bg-muted/50 cursor-pointer"
                  data-testid={`admin-order-row-${o.order_number}`}
                >
                  <td className="px-4 py-3 font-semibold text-primary">
                    <span className="flex items-center gap-2">
                      {o.order_number}
                      {!o.seen_by_admin && (
                        <span className="text-[9px] uppercase tracking-wide bg-accent text-accent-foreground px-1.5 py-0.5">New</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>{o.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{o.phone}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                    {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </td>
                  <td className="px-4 py-3 font-semibold">{inr(o.total)}</td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-xs">{o.payment_method === "cod" ? "COD" : o.payment_status === "paid" ? "Paid" : "Online (pending)"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 font-semibold whitespace-nowrap ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="admin-order-drawer">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-background h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-secondary/60 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-border">
              <div>
                <p className="font-display text-xl text-primary">{selected.order_number}</p>
                <p className="text-xs text-muted-foreground">{new Date(selected.created_at).toLocaleString("en-IN")}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="admin-drawer-close"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">Update status</p>
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      disabled={updating || selected.status === s}
                      onClick={() => updateStatus(selected, s)}
                      className={`px-3 py-2.5 text-sm border transition-colors ${
                        selected.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary hover:bg-secondary"
                      } ${s === "Cancelled" && selected.status !== s ? "text-destructive" : ""} disabled:opacity-100`}
                      data-testid={`admin-set-${s.replace(/ /g, "-")}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {updating && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> updating…</p>}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">Customer</p>
                <p className="text-sm font-semibold">{selected.customer_name}</p>
                <p className="text-sm text-muted-foreground">{selected.email}</p>
                <p className="text-sm text-muted-foreground">{selected.phone}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {selected.shipping.address}, {selected.shipping.city}, {selected.shipping.state} — {selected.shipping.pincode}
                </p>
                {selected.notes && <p className="text-sm italic text-muted-foreground mt-2">“{selected.notes}”</p>}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">Items</p>
                <div className="space-y-3">
                  {selected.items.map((i, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="w-12 aspect-[4/5] bg-muted overflow-hidden shrink-0">
                        <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm leading-tight">{i.name}</p>
                        <p className="text-xs text-muted-foreground">Qty {i.qty}{i.variant ? ` · ${i.variant}` : ""}</p>
                      </div>
                      <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border mt-4 pt-3 space-y-1.5">
                  {selected.discount > 0 && (
                    <div className="flex justify-between text-sm text-primary" data-testid="admin-order-discount">
                      <span>Coupon ({selected.coupon_code})</span>
                      <span className="font-semibold">− {inr(selected.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-semibold">Total ({selected.payment_method === "cod" ? "COD" : "Online"})</span>
                    <span className="font-semibold text-primary">{inr(selected.total)}</span>
                  </div>
                </div>
              </div>
              {selected.status_history && (
                <div>
                  <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">History</p>
                  <div className="space-y-2">
                    {[...selected.status_history].reverse().map((h, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-foreground/80">{h.status} — {h.note}</span>
                        <span className="text-muted-foreground">{new Date(h.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Products tab ---------------- */
function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState({ ...product });
  const [images, setImages] = useState(product.images || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(resolveImg(data.url));
      }
      setImages((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (images.length === 0) return toast.error("Add at least one image");
    setSaving(true);
    try {
      await api.patch(`/admin/products/${product.id}`, {
        name: form.name,
        price: Number(form.price),
        mrp: Number(form.mrp),
        description: form.description,
        material: form.material,
        dimensions: form.dimensions,
        care: form.care,
        in_stock: !!form.in_stock,
        featured: !!form.featured,
        images,
      });
      toast.success("Product updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, k, type = "text", area }) => (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
      {area ? (
        <textarea value={form[k] || ""} onChange={set(k)} rows={3} className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary resize-none" data-testid={`prod-${k}`} />
      ) : (
        <input type={type} value={form[k] ?? ""} onChange={set(k)} className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid={`prod-${k}`} />
      )}
    </label>
  );

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="product-editor">
      <div className="absolute inset-0 bg-foreground/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-background h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-secondary/60 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-border">
          <p className="font-display text-xl text-primary">Edit product</p>
          <button onClick={onClose} data-testid="prod-editor-close"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Images */}
          <div>
            <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-2">Images</p>
            <div className="flex flex-wrap gap-3">
              {images.map((img, idx) => (
                <div key={idx} className="relative w-20 h-24 bg-muted overflow-hidden">
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setImages(images.filter((_, i) => i !== idx))}
                    className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-1"
                    data-testid={`prod-img-remove-${idx}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-20 h-24 border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                data-testid="prod-img-upload"
              >
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                <span className="text-[10px] mt-1">Upload</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFile} className="hidden" />
          </div>

          <Field label="Name" k="name" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (₹)" k="price" type="number" />
            <Field label="MRP (₹)" k="mrp" type="number" />
          </div>
          <Field label="Description" k="description" area />
          <Field label="Material" k="material" />
          <Field label="Dimensions" k="dimensions" />
          <Field label="Care" k="care" area />
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.in_stock} onChange={set("in_stock")} data-testid="prod-in_stock" /> In stock
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.featured} onChange={set("featured")} data-testid="prod-featured" /> Featured
            </label>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-primary text-primary-foreground py-3.5 text-sm tracking-wide flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="prod-save"
          >
            {saving && <Loader2 size={16} className="animate-spin" />} Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((p) => (
          <button
            key={p.id}
            onClick={() => setEditing(p)}
            className="text-left border border-border hover:border-primary transition-colors group"
            data-testid={`admin-product-${p.id}`}
          >
            <div className="aspect-[4/5] bg-muted overflow-hidden">
              <img src={resolveImg(p.images[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-3">
              <p className="text-sm font-medium leading-tight line-clamp-2">{p.name}</p>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-sm font-semibold text-primary">{inr(p.price)}</span>
                {p.rating_count > 0 && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Star size={11} className="text-accent fill-accent" /> {p.rating_avg}
                  </span>
                )}
              </div>
              {!p.in_stock && <span className="text-[10px] text-destructive uppercase tracking-wide">Out of stock</span>}
            </div>
          </button>
        ))}
      </div>
      {editing && (
        <ProductEditor product={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </>
  );
}

/* ---------------- Coupons tab ---------------- */
const EMPTY_COUPON = { code: "", type: "percent", value: "", min_order: "", max_discount: "", usage_limit: "", expires_at: "" };

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY_COUPON);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value) return toast.error("Code and discount value are required");
    setCreating(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code,
        type: form.type,
        value: Number(form.value),
        min_order: form.min_order ? Number(form.min_order) : 0,
        max_discount: form.max_discount ? Number(form.max_discount) : null,
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        expires_at: form.expires_at || null,
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setForm(EMPTY_COUPON);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      toast.success(`Coupon ${c.code} ${c.active ? "paused" : "activated"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("Coupon deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fmtValue = (c) => (c.type === "percent" ? `${c.value}%` : inr(c.value));

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">
          Share these codes on Instagram or WhatsApp to pull in orders.
        </p>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm"
          data-testid="coupon-new-btn"
        >
          <Plus size={16} /> New Coupon
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="border border-border bg-secondary/30 p-5 mb-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="coupon-form">
          <label className="block col-span-2 md:col-span-1">
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Code</span>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME10"
              className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary uppercase" data-testid="coupon-code-input" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Type</span>
            <select value={form.type} onChange={set("type")} className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="coupon-type-select">
              <option value="percent">% off</option>
              <option value="flat">₹ flat off</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">{form.type === "percent" ? "Percent off" : "Amount (₹)"}</span>
            <input type="number" value={form.value} onChange={set("value")} placeholder={form.type === "percent" ? "10" : "200"}
              className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="coupon-value-input" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Min order (₹)</span>
            <input type="number" value={form.min_order} onChange={set("min_order")} placeholder="0"
              className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="coupon-minorder-input" />
          </label>
          {form.type === "percent" && (
            <label className="block">
              <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Max discount (₹)</span>
              <input type="number" value={form.max_discount} onChange={set("max_discount")} placeholder="Optional"
                className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="coupon-maxdiscount-input" />
            </label>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Usage limit</span>
            <input type="number" value={form.usage_limit} onChange={set("usage_limit")} placeholder="Unlimited"
              className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="coupon-limit-input" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Expires on</span>
            <input type="date" value={form.expires_at} onChange={set("expires_at")}
              className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="coupon-expiry-input" />
          </label>
          <div className="flex items-end">
            <button type="submit" disabled={creating}
              className="w-full bg-primary text-primary-foreground py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="coupon-create-btn">
              {creating && <Loader2 size={14} className="animate-spin" />} Create
            </button>
          </div>
        </form>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left">
            <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3 hidden sm:table-cell">Min order</th>
              <th className="px-4 py-3">Used</th>
              <th className="px-4 py-3 hidden md:table-cell">Expires</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground" data-testid="coupons-empty">No coupons yet — create your first promo code.</td></tr>
            ) : (
              coupons.map((c) => (
                <tr key={c.id} className="border-t border-border" data-testid={`coupon-row-${c.code}`}>
                  <td className="px-4 py-3 font-semibold text-primary">
                    <span className="inline-flex items-center gap-1.5"><Tag size={13} /> {c.code}</span>
                  </td>
                  <td className="px-4 py-3">
                    {fmtValue(c)}{c.type === "percent" && c.max_discount ? ` (max ${inr(c.max_discount)})` : ""}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">{c.min_order ? inr(c.min_order) : "—"}</td>
                  <td className="px-4 py-3">{c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Never"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(c)}
                      className={`text-xs px-2.5 py-1 font-semibold ${c.active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}
                      data-testid={`coupon-toggle-${c.code}`}
                    >
                      {c.active ? "Active" : "Paused"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(c)} className="text-muted-foreground hover:text-destructive" data-testid={`coupon-delete-${c.code}`}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------- Instagram tab ---------------- */
function InstagramTab() {
  const [posts, setPosts] = useState([]);
  const [image, setImage] = useState("");
  const [caption, setCaption] = useState("");
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/instagram").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(resolveImg(data.url));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!image) return toast.error("Upload a photo first");
    setSaving(true);
    try {
      await api.post("/admin/instagram", { image, caption, link });
      toast.success("Added to your homepage gallery");
      setImage(""); setCaption(""); setLink("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the homepage gallery?")) return;
    try {
      await api.delete(`/admin/instagram/${p.id}`);
      toast.success("Photo removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <>
      <p className="text-sm text-muted-foreground mb-6">
        These photos appear in the "Real homes, styled simply" gallery on your homepage. Each links to your Instagram — keep 4–8 of your best styled shots here.
      </p>

      <form onSubmit={add} className="border border-border bg-secondary/30 p-5 mb-8 flex flex-wrap items-end gap-4" data-testid="insta-form">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-2">Photo</p>
          {image ? (
            <div className="relative w-24 h-24">
              <img src={resolveImg(image)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImage("")} className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full p-1">
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              data-testid="insta-upload-btn"
            >
              {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
              <span className="text-[10px] mt-1">Upload</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </div>
        <label className="block flex-1 min-w-[180px]">
          <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Caption (optional)</span>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="A quiet corner, elevated."
            className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="insta-caption-input" />
        </label>
        <label className="block flex-1 min-w-[180px]">
          <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Instagram post link (optional)</span>
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://instagram.com/p/…"
            className="mt-1 w-full bg-muted px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="insta-link-input" />
        </label>
        <button type="submit" disabled={saving}
          className="bg-primary text-primary-foreground px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60" data-testid="insta-add-btn">
          {saving && <Loader2 size={14} className="animate-spin" />} Add Photo
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {posts.length === 0 ? (
          <p className="col-span-full text-muted-foreground py-10 text-center" data-testid="insta-empty">No photos yet — add your first styled shot above.</p>
        ) : (
          posts.map((p) => (
            <div key={p.id} className="group relative aspect-square bg-muted overflow-hidden border border-border" data-testid={`insta-admin-post-${p.id}`}>
              <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover" />
              {p.caption && (
                <p className="absolute bottom-0 inset-x-0 bg-foreground/60 text-background text-[11px] px-2 py-1.5 line-clamp-1">{p.caption}</p>
              )}
              <button
                onClick={() => remove(p)}
                className="absolute top-2 right-2 bg-background/90 text-destructive rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`insta-delete-${p.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

/* ---------------- Messages tab ---------------- */
function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/messages")
      .then((r) => setMessages(r.data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setLoading(false));
  }, []);

  const markRead = async (msg) => {
    if (msg.read) return;
    try {
      await api.patch(`/admin/messages/${msg.id}/read`);
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, read: true } : m));
      if (selected?.id === msg.id) setSelected({ ...msg, read: true });
    } catch {}
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="messages-loading">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading messages…
      </div>
    );
  }

  return (
    <>
      {unreadCount > 0 && (
        <div className="flex items-center gap-2 bg-accent/15 border border-accent/40 px-5 py-3 mb-6" data-testid="messages-unread-banner">
          <Mail size={16} className="text-accent" />
          <span className="text-sm">You have <strong>{unreadCount}</strong> unread message{unreadCount > 1 ? "s" : ""}.</span>
        </div>
      )}

      {messages.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="messages-empty">
          <MessageSquare size={36} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No contact messages yet.</p>
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="messages-table">
            <thead className="bg-secondary/50 text-left">
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 hidden sm:table-cell">Email</th>
                <th className="px-4 py-3 hidden md:table-cell">Subject</th>
                <th className="px-4 py-3 hidden lg:table-cell">Message</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => { setSelected(m); markRead(m); }}
                  className={`border-t border-border hover:bg-muted/50 cursor-pointer ${!m.read ? "bg-accent/5 font-medium" : ""}`}
                  data-testid={`message-row-${m.id}`}
                >
                  <td className="px-4 py-3">
                    {!m.read && <span className="block w-2 h-2 rounded-full bg-accent" />}
                  </td>
                  <td className="px-4 py-3">{m.name}</td>
                  <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">{m.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground line-clamp-1">{m.subject || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">
                    <span className="line-clamp-1 max-w-[260px] inline-block">{m.message}</span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {new Date(m.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="message-drawer">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-background h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-secondary/60 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-border">
              <div>
                <p className="font-display text-xl text-primary">{selected.name}</p>
                <p className="text-xs text-muted-foreground">{new Date(selected.created_at).toLocaleString("en-IN")}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="message-drawer-close"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">From</p>
                <p className="text-sm font-semibold">{selected.name}</p>
                <a href={`mailto:${selected.email}`} className="text-sm text-primary hover:underline">{selected.email}</a>
              </div>
              {selected.subject && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">Subject</p>
                  <p className="text-sm">{selected.subject}</p>
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">Message</p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
              {selected.read && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-2">
                  <Eye size={13} /> Marked as read
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Main ---------------- */
export default function Admin() {
  const [tab, setTab] = useState("orders");

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Aethel Decor</p>
        <h1 className="mt-2 text-3xl sm:text-4xl text-primary font-medium">Admin Dashboard</h1>
      </div>

      <div className="flex gap-6 border-b border-border mb-8 overflow-x-auto">
        {[["orders", "Orders"], ["products", "Products"], ["coupons", "Coupons"], ["messages", "Messages"], ["emails", "Emails"], ["tickets", "Tickets"], ["vendors", "Vendors"], ["instagram", "Instagram"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`pb-3 text-sm tracking-wide -mb-px border-b-2 transition-colors whitespace-nowrap ${
              tab === key ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-primary"
            }`}
            data-testid={`admin-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "orders" ? <OrdersTab /> : tab === "products" ? <AdminProductsTab /> : tab === "coupons" ? <CouponsTab /> : tab === "messages" ? <MessagesTab /> : tab === "emails" ? <AdminEmailTab /> : tab === "tickets" ? <AdminTicketsTab /> : tab === "vendors" ? <AdminVendorsTab /> : <InstagramTab />}
    </div>
  );
}
